/**
 * The extension lane: searches made by the site inside the Chrome extension's
 * side panel.
 *
 * Kasada's BotID judges any page framed by an extension a bot (measured
 * 2026-10-14 in a real Chrome: the same search passes in a tab and gets 403 in
 * the panel's frame), so the side panel could never search. The framed page
 * marks its own search requests with the extension origin it sits in, and
 * /api/product-search lets a marked request through a bot verdict within hard
 * limits (server/search/extension-lane.ts).
 *
 * The marker is not a proof: the extension's code is public, so anyone can send
 * it. The limits are what bound the cost of someone who does.
 */

export const EXTENSION_FRAME_HEADER = 'x-inception-frame';

/** Searches one IP can run through the lane per hour. */
export const EXTENSION_LANE_PER_IP_PER_HOUR = 15;
/** Searches the lane admits per UTC day, all IPs together: the spend ceiling. */
export const EXTENSION_LANE_PER_DAY = 300;

const EXTENSION_ORIGIN = /^chrome-extension:\/\/[a-p]{32}$/;

export function isExtensionOrigin(value: string | null | undefined): value is string {
  return typeof value === 'string' && EXTENSION_ORIGIN.test(value);
}

/**
 * The Chrome extension this page is framed by, from the browser's own record of
 * its ancestors (`location.ancestorOrigins`, nearest first), or null.
 */
export function extensionAncestor(location: { ancestorOrigins?: ArrayLike<string> | null }): string | null {
  const ancestors = location.ancestorOrigins;
  if (!ancestors || ancestors.length === 0) return null;
  const top = ancestors[ancestors.length - 1];
  return isExtensionOrigin(top) ? top : null;
}

/**
 * The requests that carry the marker: the page's own search turns (the lane at
 * the BotID gate) and the intent check (both mint the search-action challenge,
 * which a framed page needs as a partitioned cookie: search-action-challenge.ts).
 */
const MARKED_PATHS = new Set(['/api/product-search', '/api/classify-intent']);

export function isLaneRequest(url: string, pageOrigin: string): boolean {
  try {
    const u = new URL(url, pageOrigin);
    return u.origin === pageOrigin && MARKED_PATHS.has(u.pathname);
  } catch {
    return false;
  }
}
